import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import {
  customSurfaceTreatmentsQuery,
  getCustomSurfaceTreatmentsQueryData,
  setCustomSurfaceTreatmentsQueryData,
} from "#modules/companySettings/infra/customSurfaceTreatments.query";
import { deleteCustomSurfaceTreatmentApi } from "#modules/companySettings/infra/deleteCustomSurfaceTreatment.api";
import { processStepCatalogQuery } from "#modules/quoteRequestLines/infra/processStepCatalogQuery";

export const useDeleteCustomSurfaceTreatment = () => {
  const queryClient = useQueryClient();

  const { mutate, ...rest } = useMutation({
    mutationFn: deleteCustomSurfaceTreatmentApi,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: customSurfaceTreatmentsQuery(variables.companyId).queryKey,
      });
      const previous = getCustomSurfaceTreatmentsQueryData(queryClient, variables.companyId);

      if (previous) {
        setCustomSurfaceTreatmentsQueryData(
          queryClient,
          variables.companyId,
          previous.filter((row) => row.id !== variables.surfaceTreatmentId),
        );
      }

      return { previous, companyId: variables.companyId };
    },
    onError: (err: Error, variables, context) => {
      showErrorToast({ title: err.message });
      errorHandler(err);

      if (context?.previous) {
        setCustomSurfaceTreatmentsQueryData(queryClient, variables.companyId, context.previous);
      }
    },
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries(customSurfaceTreatmentsQuery(variables.companyId));
      void queryClient.invalidateQueries(processStepCatalogQuery());
    },
  });

  return { deleteCustomSurfaceTreatment: mutate, ...rest };
};
